import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Divider } from 'base-ui'

import { priceGetFormattedPrice } from '../../../../price'
import { ProductPicture } from '../../../../product'
import { priceAccessorGetDisplayPrice } from '../../../model/priceAccessor/priceAccessorGetDisplayPrice'
import { productAccessorGetProductNameWithPromotionName } from '../../../model/productAccessor/productAccessorGetProductNameWithPromotionName'
import { productAccessorIsLifestyleProduct } from '../../../model/productAccessor/productAccessorIsLifestyleProduct'
import { useProductPageContext } from '../../productPageContext'

import { FixedPriceDifferencePanel } from './FixedPriceDifferencePanel'
import { LifeStyleProductPanel } from './LifeStyleProductPanel'

export const ProductPricingProductSection: FC = () => {
  const { currentOffer, currentPrescriptionType, product } =
    useProductPageContext()
  const i18n = useIntl()
  if (!currentOffer) {
    return null
  }
  const isLifestyleProduct: boolean = productAccessorIsLifestyleProduct(product)

  const title = productAccessorGetProductNameWithPromotionName(
    currentOffer,
    product,
  )

  // WSAWA-4799 - we get all the prices of the offer based on the selected value from the PDP Prescription dropdown
  const productPrescriptionPrice = priceAccessorGetDisplayPrice(
    currentOffer,
    currentPrescriptionType,
  )

  return (
    <>
      <div className="prose mb-2">
        <h3>
          <FormattedMessage id="productPricing.modal.productSection.title" />
        </h3>
      </div>
      <div className="flex flex-col rounded-lg bg-light-primary-low p-4 tablet:gap-1">
        <div className="my-0 flex items-center gap-4 self-stretch">
          <ProductPicture
            className="inline-flex size-20 items-start justify-start rounded-md bg-light-primary-medium p-0"
            imageProps={{
              alt: product.name,
              height: 80,
              src: product.images?.at(0) ?? '',
              width: 80,
            }}
            prescription
          />
          <h4 className="ml-2 mt-0">{title}</h4>
        </div>
        <p className="mb-3 mt-4 text-dark-primary-medium">
          <FormattedMessage id="productPricing.modal.productSection.totalExplanation" />
        </p>
        {productPrescriptionPrice &&
        productPrescriptionPrice.coPayment !== null ? (
          <div className="flex flex-row justify-between">
            <p className="my-1">
              <FormattedMessage id="productPricing.modal.productSection.statutoryCoPayment" />
            </p>
            <span>
              {priceGetFormattedPrice(
                productPrescriptionPrice.coPayment ?? 0,
                i18n,
              )}
            </span>
          </div>
        ) : null}
        {productPrescriptionPrice?.fixedPriceDifference ? (
          <div className="flex flex-row justify-between">
            <p className="my-1">
              <FormattedMessage id="productPricing.modal.productSection.fixedAmountDifference" />
            </p>
            <span>
              {priceGetFormattedPrice(
                productPrescriptionPrice.fixedPriceDifference,
                i18n,
              )}
            </span>
          </div>
        ) : null}
        {isLifestyleProduct ? (
          <div className="flex flex-row justify-between">
            <p className="my-1">
              <FormattedMessage id="productPricing.modal.productSection.pharmacySellingPrice" />
            </p>
            <span>
              {priceGetFormattedPrice(
                productPrescriptionPrice?.price ?? 0,
                i18n,
              )}
            </span>
          </div>
        ) : null}

        <p className="my-1">
          <Divider />
        </p>
        <div className="flex flex-row justify-between">
          <p className="mb-0 mt-1">
            <FormattedMessage id="productPricing.modal.productSection.yourPrice" />
          </p>
          <span>
            {priceGetFormattedPrice(
              productPrescriptionPrice?.totalPrice ?? 0,
              i18n,
            )}
          </span>
        </div>
      </div>
      {productPrescriptionPrice?.fixedPriceDifference ? (
        <FixedPriceDifferencePanel />
      ) : null}
      {isLifestyleProduct ? <LifeStyleProductPanel /> : null}
    </>
  )
}
